import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";

import { axiosGet, axiosPost } from "../../api/Axios";
import { formatDateInMMDDYYYY, formatDateInYYYYMMDD } from "../../helpers/date";
function calculateAverage(arr: number[]) {
  if (arr.length === 0) return 0; // To handle empty arrays
  const sum = arr.reduce((acc, val) => acc + val, 0);
  const average = sum / arr.length;
  return Math.round(average);
}

export const getPainTrackingEntries = createAsyncThunk(
  "/painTracking/getPainTrackingEntries",
  async (filter: number, { rejectWithValue }) => {
    try {
      const response = await axiosGet<TrackingEntryApiResponse>(
        `/painTracking/getPainTrackingEntries?page=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

export const getAveragePainRating = createAsyncThunk(
  "/painTracking/getAveragePainRating",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<AveragePainRatingDashboard>(
        `/painTracking/getAveragePainRating?days=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);
export const getUserPainLocations = createAsyncThunk(
  "/painTracking/getUserPainLocations",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<UserPainData>(
        `/painTracking/getUserPainLocations?days=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

export const calculateReliefMethodEffectiveness = createAsyncThunk(
  "/painTracking/calculateReliefMethodEffectiveness",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<ReliefMethodEffectivenessResponse[]>(
        `/painTracking/calculateReliefMethodEffectiveness?days=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

export const getFrequencyOfPainTriggersWithRating = createAsyncThunk(
  "/painTracking/getFrequencyOfPainTriggersWithRating",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<PainFrequencyApiResponse[]>(
        `/painTracking/getFrequencyOfPainTriggersWithRating?days=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

export const getUserPainEnergyMoodHeatMap = createAsyncThunk(
  "/painTracking/getUserPainEnergyMoodHeatMap",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<HeatMapApiResponse[]>(
        `/painTracking/getUserPainEnergyMoodHeatMap?date=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

export const getMySymptomTrackingReport = createAsyncThunk(
  "/painTracking/getMySymptomTrackingReport",
  async (filter: string, { rejectWithValue }) => {
    try {
      const response = await axiosGet<SymptomFrequencyApiResponse[]>(
        `/painTracking/getMySymptomTrackingReport?days=${filter}`
      ); // Replace with your profile endpoint
      return response; // Return the user profile data
    } catch (error) {
      return rejectWithValue(error); // Return the error message
    }
  }
);

const initialState: IDashboardSliceProp = {
  errors: [],
  isError: false,
  isTrackingApiLoading: true,
  trackingEntriesData: {
    totalPages: 0,
    currentPage: 1,
    previousPage: null,
    nextPage: null,
    totalCount: 0,
    pageSize: 50,
    entries: [],
  },
  isRatingApiLoading: true,
  averagePainRatingData: [],
  lastRecordedEntryDate: null,
  averagePainRatings: [],
  averagePainRatingDates: [],
  overAllAverageRating: 1,
  reliefEffectivenessAverage: [],
  isReliefEffectiveAverageLoading: true,
  isPainFrequencyReportLoading: true,
  painFrequencyReportData: [],
  isSymptomsFrequencyReportLoading: true,
  symptomFrequencyData: [],
  isHeatMapLoading: true,
  painEnergyHeatMap: [],
  isPainLocationsLoading: true,
  userPainData: {
    painLocations: [],
    painTypes: [],
    triggerTracking: [],
    notes: [],
    locationAverages: {},
  },
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    resetPainLocationsAtCreateForm: (state, action: PayloadAction<number>) => {
      state.userPainData = {
        painLocations: [],
        painTypes: [],
        triggerTracking: [],
        notes: [],
        locationAverages: {},
      };
    },
    updatePainLocationsForOrderDetails: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.userPainData.painLocations = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getPainTrackingEntries.pending, (state) => {
        state.isTrackingApiLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(getPainTrackingEntries.fulfilled, (state, action) => {
        state.isTrackingApiLoading = false;
        state.trackingEntriesData = action.payload;
        // state.averagePainRatingData = action.payload;
      })
      .addCase(getPainTrackingEntries.rejected, (state, action) => {
        state.isTrackingApiLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      })
      .addCase(getAveragePainRating.pending, (state) => {
        state.isRatingApiLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(getAveragePainRating.fulfilled, (state, action) => {
        state.isRatingApiLoading = false;
        state.averagePainRatingData = action.payload.averagePainRatingData;

        state.averagePainRatingDates = state.averagePainRatingData.map((item) =>
          formatDateInMMDDYYYY(item.date)
        );

        state.averagePainRatings = state.averagePainRatingData.map(
          (item) => item.averagePainRating
        );
        state.lastRecordedEntryDate = action.payload.lastRecordedEntryDate;
        state.overAllAverageRating = calculateAverage(state.averagePainRatings);
      })
      .addCase(getAveragePainRating.rejected, (state, action) => {
        state.isRatingApiLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      })
      .addCase(calculateReliefMethodEffectiveness.pending, (state) => {
        state.isReliefEffectiveAverageLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(
        calculateReliefMethodEffectiveness.fulfilled,
        (state, action) => {
          state.isReliefEffectiveAverageLoading = false;
          state.reliefEffectivenessAverage = action.payload;
        }
      )
      .addCase(calculateReliefMethodEffectiveness.rejected, (state, action) => {
        state.isReliefEffectiveAverageLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      })
      .addCase(getFrequencyOfPainTriggersWithRating.pending, (state) => {
        state.isPainFrequencyReportLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(
        getFrequencyOfPainTriggersWithRating.fulfilled,
        (state, action) => {
          state.isPainFrequencyReportLoading = false;
          state.painFrequencyReportData = action.payload;
        }
      )
      .addCase(
        getFrequencyOfPainTriggersWithRating.rejected,
        (state, action) => {
          state.isPainFrequencyReportLoading = false;
          state.isError = true; // Handle error message
          state.errors = [action.payload as string];
        }
      )
      .addCase(getUserPainLocations.pending, (state) => {
        state.isPainLocationsLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(getUserPainLocations.fulfilled, (state, action) => {
        state.isPainLocationsLoading = false;
        state.userPainData = action.payload;
      })
      .addCase(getUserPainLocations.rejected, (state, action) => {
        state.isPainLocationsLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      })
      .addCase(getMySymptomTrackingReport.pending, (state) => {
        state.isSymptomsFrequencyReportLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(getMySymptomTrackingReport.fulfilled, (state, action) => {
        state.isSymptomsFrequencyReportLoading = false;
        state.symptomFrequencyData = action.payload;
      })
      .addCase(getMySymptomTrackingReport.rejected, (state, action) => {
        state.isSymptomsFrequencyReportLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      })
      .addCase(getUserPainEnergyMoodHeatMap.pending, (state) => {
        state.isHeatMapLoading = true;
        state.isError = false; // Reset error
      })
      .addCase(getUserPainEnergyMoodHeatMap.fulfilled, (state, action) => {
        state.isHeatMapLoading = false;
        state.painEnergyHeatMap = action.payload;
      })
      .addCase(getUserPainEnergyMoodHeatMap.rejected, (state, action) => {
        state.isHeatMapLoading = false;
        state.isError = true; // Handle error message
        state.errors = [action.payload as string];
      });
  },
});

export const {
  resetPainLocationsAtCreateForm,
  updatePainLocationsForOrderDetails,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
